import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Seo from './Seo';

const routesMeta = [
  { pattern: '/', title: 'Uniofunds — Empowering Community Finance', desc: 'Uniofunds empowers communities through financial collaboration and inclusion.' },
  { pattern: '/about-us', title: 'About Uniofunds', desc: 'Learn about Uniofunds and our mission to drive inclusive community finance.' },
  { pattern: '/contact-us', title: 'Contact Uniofunds', desc: 'Get in touch with the Uniofunds team.' },
  { pattern: '/features', title: 'Uniofunds Features', desc: 'Explore Uniofunds features for groups, fundraising, events, and more.' },
  { pattern: '/faq', title: 'Uniofunds FAQ', desc: 'Answers to frequently asked questions about Uniofunds.' },
  { pattern: '/careers', title: 'Careers at Uniofunds', desc: 'Join Uniofunds and help build inclusive financial tools for communities.' },
  { pattern: '/fundraise-campaigns', title: 'Fundraise Campaigns — Uniofunds', desc: 'Browse active fundraising campaigns on Uniofunds.' },
  { pattern: '/fundraise-campaigns/:id', title: 'Campaign Details — Uniofunds', desc: 'See details for a Uniofunds fundraising campaign.' },
  { 
    pattern: '/events', 
    title: 'Financial Empowerment Events | Uniofunds', 
    desc: "Discover free financial events, workshops, webinars, and fundraisers by Uniofunds on budgeting, investing, debt management, and more. Join community-driven sessions to build financial skills and achieve your goals."
  },
  // Updated events page metadata to reflect richer content
  { pattern: '/events/:id', title: 'Event Details — Uniofunds', desc: 'Learn more about a specific Uniofunds event.' },
  // Resources (from sitemap)
  { pattern: '/resources/articles', title: 'Financial Articles & Guides | Uniofunds', desc: 'Educational articles, how-tos, and guides to help you navigate group savings, debt, credit, and budgeting.' },
  { pattern: '/resources/debt-management', title: 'Debt Management Tools | Uniofunds', desc: 'Interactive calculators for payoff strategies, consolidation, risk indicators, and credit utilization.' },
  { pattern: '/resources/credit-tools', title: 'Credit Tools & Education | Uniofunds', desc: 'Explore credit score simulators, report helpers, inquiry guidance, dispute letters, and more.' },
  { pattern: '/resources/budget-tools', title: 'Budgeting Tools Suite | Uniofunds', desc: 'Zero-based planner, 50/30/20 visualizer, cash flow stress test, expense categorizer, and bill calendar.' },
  { pattern: '/resources/retirement-calculator', title: 'Retirement Calculator | Uniofunds', desc: 'Estimate retirement savings with adjustable contributions, growth, and inflation assumptions.' },
];

const RouteSEO = () => {
  const { pathname } = useLocation();
  const siteUrl = 'https://www.uniofunds.com';
  const defaultOgImage = `${siteUrl}/favicon.ico`;

  const meta = routesMeta.find(({ pattern }) => matchPath({ path: pattern, end: true }, pathname));

  if (!meta) return null;

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.desc}
        canonical={`${siteUrl}${pathname}`}
        image={meta.image || defaultOgImage}
      />
      {pathname === '/' && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Uniofunds',
            alternateName: ['UnioFunds', 'UNIOFUNDS', 'Unio Funds', 'unio funds', 'uniofund'],
            url: siteUrl,
            logo: `${siteUrl}/assets/logo/symbol.png`,
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Uniofunds',
            alternateName: ['UnioFunds', 'UNIOFUNDS', 'Unio Funds', 'unio funds', 'uniofund'],
            url: siteUrl,
            potentialAction: {
              '@type': 'SearchAction',
              target: `${siteUrl}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          })}</script>
        </Helmet>
      )}
    </>
  );
};

export default RouteSEO;
